'use client';
import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

/*
 * <ScrollRevealText>
 *
 * Apple/Stripe-style text reveal: as the paragraph scrolls through the
 * viewport, each word's color shifts word-by-word from a muted resting
 * state to a target colour. Reads as if a highlighter is sweeping across.
 *
 * ── PROPS ─────────────────────────────────────────────────────────────
 *
 * children: string                — the text to animate.
 * className: string?              — applied to the wrapping <p>.
 * style: CSSProperties?           — inline style on the wrapping <p>.
 * mutedColor: string?             — resting word colour. Default
 *                                   `rgba(15, 23, 42, 0.18)` (light gray).
 * revealColor: string?            — final colour each word lands on.
 *                                   Default `var(--text-primary)`.
 * range: [string, string]?        — scroll offsets that drive the reveal,
 *                                   in the framer-motion `useScroll`
 *                                   format. Default ['start 0.85',
 *                                   'start 0.35'] — i.e. the reveal
 *                                   starts when the paragraph's top is
 *                                   85% down the viewport and finishes
 *                                   when it crosses 35% from the top.
 * as: string?                     — tag for the wrapping element.
 *                                   Defaults to 'p'. Use 'h2', 'div',
 *                                   etc. to fit context.
 *
 * The wrapping element is a regular DOM tag (NOT a motion element) so
 * existing CSS — font-size, line-height, text-align, etc. — applies as
 * normal. Each word becomes a `motion.span` whose `color` is driven by
 * a scroll-linked `useTransform`.
 */

export function ScrollRevealText({
  children,
  className,
  style,
  mutedColor = 'rgba(15, 23, 42, 0.18)',
  revealColor = '#000000',
  range = ['start 0.85', 'start 0.35'],
  as: As = 'p',
}) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: range,
  });

  const text = String(children ?? '');
  const words = text.split(/(\s+)/); // keep whitespace tokens so spacing renders correctly
  const wordCount = words.filter((w) => w.trim().length > 0).length;

  let wordIndex = -1;

  return (
    <As ref={ref} className={className} style={style}>
      {words.map((token, i) => {
        if (token.trim().length === 0) {
          return <span key={i}>{token}</span>;
        }
        wordIndex += 1;
        const start = wordIndex / Math.max(wordCount, 1);
        const end = (wordIndex + 1) / Math.max(wordCount, 1);
        return (
          <Word
            key={`w-${wordIndex}`}
            word={token}
            progress={scrollYProgress}
            from={start}
            to={end}
            mutedColor={mutedColor}
            revealColor={revealColor}
          />
        );
      })}
    </As>
  );
}

function Word({ word, progress, from, to, mutedColor, revealColor }) {
  const color = useTransform(progress, [from, to], [mutedColor, revealColor]);
  return <motion.span style={{ color }}>{word}</motion.span>;
}
